import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface RememberMeCredentials {
  username: string;
  password: string;
}

export type StoredMenuJsonItem = unknown;

export interface StoredProfileImageDetails {
  id: number | null;
  filename: string | null;
  filepath: string | null;
  uploaded_file: string | null;
}

export interface StoredProfileDetails {
  user_id?: number | string | null;
  username?: string | null;
  user_fullname: string | null;
  email: string | null;
  mobile_no: string | null;
  role_name: string | null;
  substation_id?: number | null;
  profile_image: StoredProfileImageDetails | null;
}

@Injectable({
  providedIn: 'root',
})
export class ManageSession {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cookieName = 'psod_remember_me';
  private readonly tokenKey = 'psod_auth_token';
  private readonly cookieDays = 7;

  saveRememberMeCredentials(credentials: RememberMeCredentials): void {
    if (!this.isBrowser()) {
      return;
    }

    const payload = btoa(
      encodeURIComponent(JSON.stringify(credentials)),
    );
    const expires = this.buildExpiry(this.cookieDays);

    document.cookie = [
      `${this.cookieName}=${payload}`,
      `expires=${expires}`,
      'path=/',
      'SameSite=Lax',
    ].join('; ');
  }

  getRememberMeCredentials(): RememberMeCredentials | null {
    if (!this.isBrowser()) {
      return null;
    }

    const cookieValue = this.readCookie(this.cookieName);
    if (!cookieValue) {
      return null;
    }

    try {
      const decoded = decodeURIComponent(atob(cookieValue));
      const parsed = JSON.parse(decoded) as Partial<RememberMeCredentials>;

      if (typeof parsed.username !== 'string' || typeof parsed.password !== 'string') {
        return null;
      }

      return {
        username: parsed.username,
        password: parsed.password,
      };
    } catch {
      return null;
    }
  }

  clearRememberMeCredentials(): void {
    if (!this.isBrowser()) {
      return;
    }

    document.cookie = [
      `${this.cookieName}=`,
      'expires=Thu, 01 Jan 1970 00:00:00 GMT',
      'path=/',
      'SameSite=Lax',
    ].join('; ');
  }

  saveAuthToken(token: string): void {
    if (!this.isBrowser()) {
      return;
    }

    sessionStorage.setItem(this.tokenKey, token);
  }

  getAuthToken(): string | null {
    if (!this.isBrowser()) {
      return null;
    }

    return sessionStorage.getItem(this.tokenKey);
  }

  clearAuthToken(): void {
    if (!this.isBrowser()) {
      return;
    }

    sessionStorage.removeItem(this.tokenKey);
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private readCookie(name: string): string | null {
    const prefix = `${name}=`;
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const match = cookies.find((cookie) => cookie.startsWith(prefix));

    return match ? match.slice(prefix.length) : null;
  }

  private buildExpiry(days: number): string {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + days);

    return expiry.toUTCString();
  }
}
